import { FileCheckIcon, FileIcon, Loader2Icon } from 'lucide-react';
import { useDeleteMedia } from '@features/placements/services/delete-media';
import type { TImage } from '@shared/types/image';
import { DocumentDownload } from 'iconsax-react';
import { Link } from 'react-router-dom';
import { Progress } from '@src/design-system/progress-bar';
import { useFormikContext } from 'formik';
import type { TAllStepsInitialValues } from '@shared/forms/placement';
import { Trash } from 'iconsax-react';

export function FileUploaded({
  file,
  progressValue,
  fieldName,
  values,
  viewOnly = false,
  onDelete,
  onDeleteSuccess,
  onDeleteError,
  onDeleteSettled,
}: {
  file: File | TImage;
  progressValue: number;
  values: TImage[];
  fieldName: string;
  viewOnly?: boolean;
  onDelete?: () => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
  onDeleteSettled?: () => void;
}) {
  const { mutate: deleteMedia, isPending: isDeleteMediaPending } =
    useDeleteMedia();

  const { setFieldValue } = useFormikContext<TAllStepsInitialValues>();

  const isFileSuccessfullyUploaded = 'id' in file;

  const handleDelete = () => {

    if (onDelete && viewOnly) {
      onDelete();
      return;
    }
    const filteredArr = values?.filter((img) => img.id !== (file as TImage).id);
    deleteMedia((file as TImage).id, {
      onSuccess: () => {
        onDeleteSuccess?.();
      },
      onError: () => {
        onDeleteError?.();
      },
      onSettled: () => {
        setFieldValue(fieldName, filteredArr);
        onDeleteSettled?.();
      },
    });
  };

  return (
    <div
      key={file.name}
      className='border border-pale-border bg-primary-30 rounded-lg p-2'
    >
      <div className='flex items-center gap-2'>
        {viewOnly ? <img src={(file as TImage).file} alt={file.name} className='w-10! h-10! rounded-lg!' /> : isFileSuccessfullyUploaded ? (
          <FileCheckIcon
            className='fill-gray-800 text-white'
            size={32}
          />
        ) : (
          <FileIcon
            className='fill-gray-800 text-white'
            size={32}
          />
        )}

        <div className='flex text-sm flex-col'>
          <span className='text-gray-800'>{file.name}</span>
          <span className='text-gray-600'>
            {(file.size / (1024 * 1024)).toFixed(2)} MB
          </span>
        </div>

        <div className='flex items-center ms-auto'>
          {!isFileSuccessfullyUploaded ? (
            <span className='text-sm text-secondary-1000'>
              {progressValue}%
            </span>
          ) : (
            <>
              <Link
                to={file.file}
                className='bg-transparent p-1 rounded-md transition-colors hover:bg-gray-100'
                target='_blank'
              >
                <DocumentDownload
                  size={18}
                  className='text-gray-800 cursor-pointer'
                />
              </Link>

              <span
                onClick={handleDelete}
                className='text-destructive bg-transparent p-1 rounded-md transition-colors hover:bg-red-100 cursor-pointer'
              >
                {isDeleteMediaPending ? (
                  <Loader2Icon
                    size={16}
                    className='animate-spin'
                  />
                ) : (
                  <Trash />
                )}
              </span>
            </>
          )}
        </div>
      </div>
      {!isFileSuccessfullyUploaded && (
        <Progress
          className='mt-2 h-1 flex-1 '
          value={progressValue}
        />
      )}
    </div>
  );
}
